import { ApolloError } from '@apollo/client';
import { GetPartnerCompensationQuery } from '@storefront/data-commerce';
import { Breadcrumbs } from '@veneer/core';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import styles from './breadcrumb.module.scss';

interface BreadcrumbProps {
  defaultURL: string;
  clientReferrer: string;
  partnerCompensation?: GetPartnerCompensationQuery;
  error?: ApolloError;
  onClick?: () => void;
  isSMB?: boolean;
}

export function Breadcrumb({
  partnerCompensation,
  error,
  onClick,
  defaultURL,
  clientReferrer,
  isSMB = false,
}: Readonly<BreadcrumbProps>) {
  const { t } = useTranslation();
  const allowedUrls = [
    /^https:\/\/(www.)?hp\.com(?!.*hp-all-in-plan-enroll).*$/,
  ];
  const getPartnerName = () => {
    if (error) {
      return null;
    }
    return partnerCompensation?.getPartnerCompensation?.hp_partner_name;
  };

  const getPartnerUrl = () => {
    if (error) {
      return null;
    }
    return partnerCompensation?.getPartnerCompensation
      ?.hp_off_ramp_redirect_url;
  };

  const getPartnerLogo = () => {
    if (error) {
      return null;
    }
    return partnerCompensation?.getPartnerCompensation?.hp_partner_logo_url;
  };

  const partnerName = !getPartnerName()
    ? t('breadcrumb.defaultStore')
    : getPartnerName();
  const partnerUrl = !getPartnerUrl() ? defaultURL : getPartnerUrl();
  const partnerLogo = getPartnerLogo();

  const getDynamicUrl = () => {
    let dynamicUrl = '';
    const isAllowed = allowedUrls.some((pattern) =>
      pattern.test(clientReferrer)
    );
    if (isAllowed) {
      dynamicUrl = clientReferrer;
    } else {
      dynamicUrl = partnerUrl ?? '';
    }
    return dynamicUrl;
  };

  const breadcrumbItems = [
    {
      text: partnerCompensation?.getPartnerCompensation
        ? t('breadcrumb.name', {
            partner: partnerName,
          })
        : partnerName,
      customItem: (
        <Link
          className={styles['custom-link']}
          href={getDynamicUrl()}
          onClick={onClick}
          data-testid="breadcrumb-first-item"
          target={partnerCompensation ? '_blank' : '_self'}
        />
      ),
    },
    { text: isSMB ? t('breadcrumb.pdpConfigSmb') : t('breadcrumb.pdpConfig') },
  ];

  const sanitizedBreadcrumbItems = breadcrumbItems.map((item) => ({
    text: item?.text || '',
    customItem: item?.customItem,
  }));

  return (
    <div className={styles['general-container']}>
      {partnerLogo && (
        <Link href={getDynamicUrl()} className={styles['custom-link']}>
          <Image
            src={partnerLogo}
            width={100}
            height={100}
            alt={partnerName + '-logo'}
            className={styles['partner-logo']}
            id="partner-logo"
            data-testid="partner-logo"
          />
        </Link>
      )}
      <Breadcrumbs
        aria-label="Breadcrumb with two levels"
        id="breadcrumb"
        items={sanitizedBreadcrumbItems}
        className={styles['breadcrumb']}
      />
    </div>
  );
}

export default Breadcrumb;
